import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ICatalogue } from "../../apis/useGetCatalogueData/types";
import ArrowRight from "../../assets/images/ArrowRight.svg";
import { calculateTotalBcrsDeposit } from "../../common/helpers/bcrsHelper";
import { isItemOrderLimitExceeded } from "../../common/helpers/itemOrderLimitHelper";
import { displayCentsWithDollarSign } from "../../common/helpers/stringUtils";
import { useItemSubtotal } from "../../common/hooks/useItemSubtotal";
import Condition from "../../components/Conditional/Condition";
import { Button, Image } from "../../components/elements";
import PopupDialog from "../../components/elements/PopupDialog/PopupDialog";
import BottomPadding from "../../components/padding/BottomPadding";
import {
	useCartActions,
	useCartContext,
} from "../../contexts/CartServiceContext/CartServiceContext";
import { useCatalogueContext } from "../../contexts/CatalogueContext/CatalogueContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { PAGE_PARAM } from "../constants";
import "./ProductDetailPage.scss";
import "./ProductDetailPage_flint.scss";
import "./ProductDetailPage_kokoro.scss";
import "./ProductDetailPage_pdd.scss";
import "./ProductDetailPage_shabuDays.scss";
import "./ProductDetailPage_torikizoku.scss";
import "./ProductDetailPage_wang.scss";
import ItemDescription from "./description/ItemDescription";
import ItemImage from "./description/ItemImage";
import { hasInvalidInput } from "./helpers";
import PDPSelections from "./selections/PDPSelections";
import { useProductDetailPage } from "./useProductDetailPage";
import { useProductDetailPageSelectionData } from "./useProductDetailPageSelectionData";

const emptySelection = [] as ICatalogue[];

const ProductDetailPage = () => {
	const navigate = useNavigate();
	const { item, mode, cart_item_index } = useProductDetailPage();
	const {
		data: selection,
		selectedSetItems,
		selectedCustomizations,
	} = useProductDetailPageSelectionData(item, mode, cart_item_index);
	const cartActions = useCartActions();
	const { state: cartContext } = useCartContext((state) => state);
	const { state: bcrs_deposit_rate } = useConfigContext((state) => state.config.bcrs_deposit_rate);
	const { state: itemLimitPerPax } = useConfigContext((state) => state.config.item_limit_per_pax);
	const { state: pax } = useConfigContext((state) => state.qrSession?.pax);
	const { state: catalogueProducts } = useCatalogueContext((state) => state.products);
	const subtotal = useItemSubtotal(selection ? [selection] : emptySelection);
	const [hasError, setHasError] = useState(false);
	const [showItemLimitModal, setShowItemLimitModal] = useState(false);

	const bcrsDepositAmount = useMemo(() => {
		if (!selection) return 0;

		return calculateTotalBcrsDeposit([selection], bcrs_deposit_rate);
	}, [selection, bcrs_deposit_rate]);

	// Check if main item or any potential setItems have BCRS
	const hasAnyBcrs = useMemo(() => {
		if (!item) return false;

		// Check main item
		if (item.bcrs > 0) return true;

		// Check potential setItems if they exist
		if (item.food_supplier_set_items?.length && Array.isArray(catalogueProducts)) {
			return item.food_supplier_set_items.some((setItem) => {
				const referencedProduct = catalogueProducts.find(
					(product) => String(product.id ?? "") === String(setItem.food_supplier_menu_item_id ?? "")
				);

				return referencedProduct && referencedProduct.bcrs > 0;
			});
		}

		return false;
	}, [item, catalogueProducts]);
	const actionButtonText = useMemo(
		() =>
			mode === PAGE_PARAM.NEW ? "productDetailPage.addToCart" : "productDetailPage.updateCart",
		[mode]
	);
	const { t } = useTranslation();

	useEffect(() => {
		const res = hasInvalidInput();

		if (res === hasError && res !== hasError) return;

		setHasError(res);
	}, [selection]);

	const handleClickAddToCart = useCallback(() => {
		if (mode === PAGE_PARAM.EDIT && cart_item_index !== null) {
			const originalCartItem = cartContext?.cart?.[cart_item_index];
			const updatedItem: ICatalogue = {
				...(selection as ICatalogue),
				quantity: originalCartItem?.quantity || 1,
			};

			const cartWithoutCurrentItem = (cartContext?.cart ?? []).filter(
				(_, i) => i !== cart_item_index
			);

			if (
				isItemOrderLimitExceeded(updatedItem, cartWithoutCurrentItem, itemLimitPerPax, pax ?? 0)
			) {
				setShowItemLimitModal(true);

				return;
			}

			cartActions.UPDATE_CART_ITEM({ index: cart_item_index, item: updatedItem });
			navigate(-1);

			return;
		}

		if (
			isItemOrderLimitExceeded(
				selection as ICatalogue,
				cartContext?.cart ?? [],
				itemLimitPerPax,
				pax ?? 0
			)
		) {
			setShowItemLimitModal(true);

			return;
		}

		cartActions.ADD_ITEM(selection as ICatalogue);
		navigate(-1);
	}, [
		mode,
		cart_item_index,
		selection,
		cartActions,
		navigate,
		cartContext?.cart,
		itemLimitPerPax,
		pax,
	]);

	return (
		<Condition check={!!item}>
			<div className="PDPPageContainer">
				<ItemImage img={item?.product_image ?? ""} />
				<ItemDescription item={item!} />
				<PDPSelections
					item={item!}
					selectedSetItems={selectedSetItems}
					selectedCustomizations={selectedCustomizations}
				/>
				<Condition check={cartContext.is_takeaway && item && item?.takeaway_fee > 0}>
					<h2>
						<div>{t("productDetailPage.takeaway")}</div>
					</h2>
					<div className="TakeawayContent">{`${t("productDetailPage.foodcontainer")} +${displayCentsWithDollarSign(item?.takeaway_fee)}`}</div>
				</Condition>
				<Condition check={hasAnyBcrs}>
					<div className="BcrsDisclaimer">{t("cartPage.bcrs.fullDisclaimer")}</div>
				</Condition>
				<PopupDialog
					data-testid="item-limit-exceeded-dialog"
					className="ItemLimitDialog"
					open={showItemLimitModal}
					title={t("itemLimit.exceeded.title")}
					content={<span>{t("itemLimit.exceeded.message")}</span>}
					buttons={[
						{
							text: t("itemLimit.exceeded.confirm"),
							onClick: () => setShowItemLimitModal(false),
							className: "BtnItemLimitConfirm",
						},
					]}
					onClose={() => setShowItemLimitModal(false)}
				/>
				<BottomPadding />
				<Button
					data-testid="add-to-cart-button"
					className="ActionButton"
					onClick={handleClickAddToCart}
					disabled={hasError}
				>
					<div className="Subtotal">
						<span className="MainPrice">{displayCentsWithDollarSign(subtotal)}</span>
						<Condition check={bcrsDepositAmount > 0}>
							<span className="BcrsDepositInfo">
								{" + "}
								{t("cartPage.bcrs.deposit")} {displayCentsWithDollarSign(bcrsDepositAmount)}
							</span>
						</Condition>
					</div>
					<div className="ActionLabel">
						{t(actionButtonText)}
						<Image src={ArrowRight} alt="ArrowRight" className="ArrowRight" />
					</div>
				</Button>
			</div>
		</Condition>
	);
};

export default React.memo(ProductDetailPage);
